import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { AudioPlayer } from './AudioPlayer';

const WidgetContainer = styled(motion.div)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  max-width: 100%;
  border-radius: 12px;
  background: linear-gradient(145deg, #f8fafc, #edf2f7);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 20px 0;
  transition: all 0.3s ease;
  border: 1px solid rgba(226, 232, 240, 0.8);
`;

const WidgetHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(to right, #ffffff, #f8fafc);
  border-bottom: 1px solid #e2e8f0;
`;

const WidgetTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
  
  &:before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 18px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%234a5568'%3E%3Cpath d='M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z'/%3E%3C/svg%3E") no-repeat center center;
    margin-right: 8px;
  }
`;

const Status = styled(motion.div)`
  margin-left: auto;
  font-size: 13px;
  display: flex;
  align-items: center;
  color: ${props => props.isLoading ? '#3182ce' : props.isError ? '#e53e3e' : '#48bb78'};
  font-weight: 500;
  background: ${props => props.isLoading ? 'rgba(235, 244, 255, 0.7)' : 
                         props.isError ? 'rgba(254, 242, 242, 0.7)' : 
                         'rgba(240, 255, 244, 0.7)'};
  padding: 6px 10px;
  border-radius: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const StatusDot = styled(motion.span)`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.isLoading ? '#3182ce' : props.isError ? '#e53e3e' : '#48bb78'};
  margin-right: 8px;
  box-shadow: 0 0 0 rgba(${props => props.isLoading ? '49, 130, 206' : 
                                props.isError ? '229, 62, 62' : 
                                '72, 187, 120'}, 0.4);
`;

const VoiceControlsContainer = styled(motion.div)`
  padding: 16px 20px;
  background: linear-gradient(to bottom, #f8fafc, #f1f5f9);
  border-bottom: 1px solid #e2e8f0;
  overflow: hidden;
`;

const SelectGroup = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  font-size: 14px;
  margin-right: 12px;
  color: #4a5568;
  min-width: 70px;
  font-weight: 500;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #cbd5e0;
  background-color: white;
  font-size: 14px;
  flex: 1;
  max-width: 200px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #a0aec0;
  }
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  }
`;

const SettingsButton = styled(motion.button)`
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 16px;
  padding: 8px;
  color: #718096;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  background: #f7fafc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    color: #4a5568;
    background: #edf2f7;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

// Available voice options
const availableVoices = {
  'en-IN': [
    { id: 'en-IN-Neural2-A', name: 'Female (Indian)' }
  ],
  'en-GB': [
    { id: 'en-GB-Neural2-B', name: 'Male (British)' }
  ]
};

// SVG icons for pulse animation
const PulseAnimation = ({ isError }) => (
  <svg width="40" height="40" viewBox="0 0 40 40" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
    <circle cx="20" cy="20" r="15" fill="none" stroke={isError ? '#e53e3e' : '#48bb78'} strokeWidth="2" strokeDasharray="60 100" strokeLinecap="round">
      <animate attributeName="stroke-dasharray" values="60 100;10 150;60 100" dur="2s" repeatCount="indefinite" />
      <animate attributeName="stroke-width" values="2;3;2" dur="2s" repeatCount="indefinite" />
    </circle>
  </svg>
);

export const TTSWidget = ({ articleContent, apiEndpoint, voiceOptions: initialVoiceOptions, autoScroll, autoPlay = true }) => {
  const [status, setStatus] = useState('idle'); // idle, loading, ready, playing, error
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [voiceOptions, setVoiceOptions] = useState(initialVoiceOptions || { language: 'en-IN', voice: 'en-IN-Neural2-A' });
  const [retries, setRetries] = useState(0);
  const maxRetries = 3;
  
  const fetchAudio = async () => {
    try {
      setStatus('loading');
      setError(null);
      
      // Include voice options in the hash to make different voices have different caches
      const contentToHash = articleContent + JSON.stringify(voiceOptions);
      
      // Generate a simple hash for the content to use as identifier
      const contentHash = Array.from(contentToHash)
        .reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) & 0xffffffff, 0)
        .toString(16);
      
      console.log(`Fetching audio for content hash: ${contentHash}`);
      console.log(`Using API endpoint: ${apiEndpoint}`);
      
      // First check if we already have this audio cached
      const response = await fetch(`${apiEndpoint}/api/audio/${contentHash}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Audio found with URL: ${data.audioUrl}`);
        setAudioUrl(data.audioUrl);
        setStatus('ready');
        setRetries(0); // Reset retries on success
      } else if (response.status === 404) {
        console.log('Audio not found, generating new audio...');
        // Need to generate audio
        const generateResponse = await fetch(`${apiEndpoint}/api/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: articleContent,
            voiceOptions: voiceOptions
          }),
        });
        
        if (generateResponse.ok) {
          const data = await generateResponse.json();
          console.log(`Audio generated with URL: ${data.audioUrl}`);
          setAudioUrl(data.audioUrl);
          setStatus('ready');
          setRetries(0); // Reset retries on success
        } else {
          const errorData = await generateResponse.json().catch(() => ({}));
          console.error('Failed to generate audio:', errorData);
          throw new Error(`Failed to generate audio: ${errorData.error || generateResponse.statusText}`);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch audio:', errorData);
        throw new Error(`Failed to fetch audio: ${errorData.error || response.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching audio:', err);
      setError(err.message || 'An unknown error occurred');
      setStatus('error');
      
      // Auto-retry up to maxRetries times with exponential backoff
      if (retries < maxRetries) {
        const nextRetry = retries + 1;
        setRetries(nextRetry);
        const delay = Math.pow(2, nextRetry) * 1000; // Exponential backoff: 2s, 4s, 8s
        console.log(`Retry ${nextRetry}/${maxRetries} in ${delay/1000}s...`);
        
        setTimeout(() => {
          console.log(`Attempting retry ${nextRetry}...`);
          fetchAudio();
        }, delay);
      }
    }
  };
  
  // Function to retry manually after error
  const handleRetry = () => {
    setRetries(0);
    fetchAudio();
  };
  
  const handleLanguageChange = (e) => {
    const language = e.target.value;
    const voice = availableVoices[language][0].id;
    setVoiceOptions({ ...voiceOptions, language, voice });
    setAudioUrl(null);
    setStatus('idle');
  };
  
  const handleVoiceChange = (e) => {
    setVoiceOptions({ ...voiceOptions, voice: e.target.value });
    setAudioUrl(null);
    setStatus('idle');
  };

  // Get appropriate status message based on state
  const getStatusMessage = () => {
    switch(status) {
      case 'loading':
        return "Preparing audio...";
      case 'error':
        return "Error loading audio";
      case 'ready':
        return "Audio ready";
      case 'playing':
        return "Now playing";
      default:
        return "Click to listen";
    }
  };
  
  return (
    <WidgetContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        y: 0,
        boxShadow: status === 'playing' 
          ? '0 15px 30px rgba(0, 0, 0, 0.15)' 
          : '0 10px 25px rgba(0, 0, 0, 0.1)'
      }}
      transition={{ duration: 0.4 }}
    >
      <WidgetHeader>
        <WidgetTitle>Article Audio</WidgetTitle>
        <Status 
          isLoading={status === 'loading'} 
          isError={status === 'error'}
          animate={status === 'loading' ? { 
            opacity: [0.8, 1, 0.8],
            scale: [0.98, 1, 0.98]
          } : {}}
          transition={{ 
            repeat: status === 'loading' ? Infinity : 0,
            duration: 1.5 
          }}
          data-loading={status === 'loading' ? "true" : "false"}
        >
          <StatusDot 
            isLoading={status === 'loading'} 
            isError={status === 'error'}
            animate={status === 'loading' ? { 
              scale: [1, 1.5, 1],
              boxShadow: [
                '0 0 0 0 rgba(49, 130, 206, 0.4)',
                '0 0 0 10px rgba(49, 130, 206, 0)',
                '0 0 0 0 rgba(49, 130, 206, 0.4)'
              ]
            } : {}}
            transition={{ 
              repeat: status === 'loading' ? Infinity : 0,
              duration: 1.5 
            }}
            data-loading={status === 'loading' ? "true" : "false"}
          />
          {getStatusMessage()}
        </Status>
        <SettingsButton 
          onClick={() => setShowSettings(!showSettings)}
          aria-label="Voice settings"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
          </svg>
        </SettingsButton>
      </WidgetHeader>
      
      <AnimatePresence>
        {showSettings && (
          <VoiceControlsContainer
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <SelectGroup>
              <Label htmlFor="language-select">Language:</Label>
              <Select 
                id="language-select" 
                value={voiceOptions.language}
                onChange={handleLanguageChange}
              >
                {Object.keys(availableVoices).map(lang => (
                  <option key={lang} value={lang}>
                    {lang === 'en-IN' ? 'English (Indian)' : 
                     lang === 'en-GB' ? 'English (UK)' : lang}
                  </option>
                ))}
              </Select>
            </SelectGroup>
            
            <SelectGroup>
              <Label htmlFor="voice-select">Voice:</Label>
              <Select 
                id="voice-select" 
                value={voiceOptions.voice}
                onChange={handleVoiceChange}
              >
                {availableVoices[voiceOptions.language].map(voice => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name}
                  </option>
                ))}
              </Select>
            </SelectGroup>
          </VoiceControlsContainer>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {status === 'error' && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ 
              padding: '16px 20px', 
              textAlign: 'center', 
              position: 'relative', 
              background: '#fff5f5', 
              borderBottom: '1px solid #fed7d7' 
            }}
          >
            <PulseAnimation isError={true} />
            <motion.p 
              style={{ 
                color: '#c53030', 
                marginBottom: '12px', 
                fontWeight: 500,
                position: 'relative',
                zIndex: 1
              }}
              animate={{ x: [0, -5, 5, -5, 5, 0] }}
              transition={{ duration: 0.5 }}
            >
              {error}
            </motion.p>
            <motion.button 
              onClick={handleRetry}
              whileHover={{ scale: 1.05, backgroundColor: '#c53030' }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                background: '#e53e3e',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                zIndex: 1
              }}
            >
              Retry
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
      
      <AudioPlayer 
        audioUrl={audioUrl}
        status={status}
        setStatus={setStatus}
        onInitialPlay={fetchAudio}
        error={error}
        autoScroll={autoScroll}
        autoPlay={autoPlay}
      />
    </WidgetContainer>
  );
}; 